import {
  ChangeDetectionStrategy,
  Component,
  type OnDestroy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  BillingEntityControls,
  BillingEntityFormGroup,
  NzPracticeProviderDetailsControls,
  NzPracticeProviderDetailsFormGroup,
  NzPracticeRequiredErrorStateMatcher,
  ProviderDetailsControls,
  ProviderDetailsFormGroup,
} from '@principle-theorem/ng-principle';
import { CurrentBrandScope } from '@principle-theorem/ng-principle-shared';
import {
  TrackByFunctions,
  TypedFormArray,
  TypedFormControl,
  TypedFormGroup,
  formControlChanges$,
  type IBreadcrumb,
} from '@principle-theorem/ng-shared';
import { Brand, Staffer } from '@principle-theorem/principle-core';
import { AI_NOTES_FEATURE } from '@principle-theorem/principle-core/features';
import {
  ACC_DENTIST_PROVIDER_TYPE,
  ACC_PROVIDER_TYPE_MAP,
  AccProviderDetails,
  AccProviderTypeCode,
  GENDERS,
  Gender,
  IBillingEntity,
  IHeidiStafferSettings,
  INzStafferPracticeProviderDetails,
  INzStafferProviderDetails,
  IStafferOnlineProfile,
  type IPractice,
  type IProviderData,
  type IStaffer,
} from '@principle-theorem/principle-core/interfaces';
import {
  DocumentReference,
  Firestore,
  IProfile,
  Region,
  filterUndefined,
  isSameRef,
  shareReplayCold,
  snapshot,
  toOptions,
  type WithRef,
} from '@principle-theorem/shared';
import { omit } from 'lodash';
import { Subject, combineLatest, defer, type Observable } from 'rxjs';
import { map, switchMap, take, takeUntil } from 'rxjs/operators';
import { CurrentStafferService } from '../current-staffer.service';

type IStafferFormData = Pick<
  IStaffer,
  | 'providerDetails'
  | 'providerCommission'
  | 'prescriberNumber'
  | 'qualifications'
  | 'onlineProfile'
  | 'billingEntities'
> & {
  nzProviderDetails: Partial<INzStafferProviderDetails>;
  nzPracticeProviderDetails: INzStafferPracticeProviderDetails[];
  heidi: IHeidiStafferSettings;
};

type IStafferOnlineProfileFormData = Omit<
  IStafferOnlineProfile,
  'profileImageURL'
>;

@Component({
  selector: 'pr-staffer-edit',
  templateUrl: './staffer-edit.component.html',
  styleUrls: ['./staffer-edit.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class StafferEditComponent implements OnDestroy {
  private _onDestroy$ = new Subject<void>();
  trackByIndex = TrackByFunctions.index();
  breadcrumbs$: Observable<IBreadcrumb[]>;
  staffer$: Observable<WithRef<IStaffer>>;
  profile$: Observable<IProfile>;
  storagePath$: Observable<string>;
  practices$: Observable<WithRef<IPractice>[]>;
  selectablePracticesByRow$: Observable<WithRef<IPractice>[][]>;
  providerDetailControls: ProviderDetailsControls;
  nzPracticeProviderDetailControls: NzPracticeProviderDetailsControls;
  billingEntityControls: BillingEntityControls;
  billingEntitiesEnabled$: Observable<boolean>;
  genders: Gender[] = GENDERS;
  form = new TypedFormGroup<IStafferFormData>({
    providerDetails: new TypedFormArray<IProviderData>([
      new ProviderDetailsFormGroup(),
    ]),
    prescriberNumber: new TypedFormControl<string>(''),
    qualifications: new TypedFormControl<string>(''),
    providerCommission: new TypedFormControl<number>(0),
    onlineProfile: new TypedFormGroup<IStafferOnlineProfileFormData>({
      name: new TypedFormControl<string>(''),
      gender: new TypedFormControl<Gender>(),
      title: new TypedFormControl<string>(''),
      bio: new TypedFormControl<string>(''),
    }),
    nzProviderDetails: new TypedFormGroup<INzStafferProviderDetails>({
      dcnzNumber: new TypedFormControl<string>('', Validators.maxLength(6)),
      acc: new TypedFormGroup<AccProviderDetails>({
        providerId: new TypedFormControl<string>('', Validators.maxLength(6)),
        providerTypeCode: new TypedFormControl<AccProviderTypeCode>(
          ACC_DENTIST_PROVIDER_TYPE,
          Validators.required
        ),
      }),
    }),
    nzPracticeProviderDetails:
      new TypedFormArray<INzStafferPracticeProviderDetails>([
        new NzPracticeProviderDetailsFormGroup(),
      ]),
    billingEntities: new TypedFormArray<IBillingEntity>([]),
    heidi: new TypedFormGroup<IHeidiStafferSettings>({
      email: new TypedFormControl<string>('', Validators.email),
      useAsDefault: new TypedFormControl<boolean>(false),
    }),
  });
  Region = Region;
  aiNotesFeature = AI_NOTES_FEATURE;
  nzMaxLengthError = 'Maximum 6 characters';
  practiceRequiredMatcher = new NzPracticeRequiredErrorStateMatcher();
  readonly providerTypeOptions = toOptions(ACC_PROVIDER_TYPE_MAP);

  constructor(
    private _currentStaffer: CurrentStafferService,
    private _snackBar: MatSnackBar,
    private _currentBrand: CurrentBrandScope
  ) {
    this.providerDetailControls = new ProviderDetailsControls(
      this.providerDetails
    );
    this.nzPracticeProviderDetailControls =
      new NzPracticeProviderDetailsControls(this.nzPracticeProviderDetails);
    this.billingEntityControls = new BillingEntityControls(
      this.billingEntities
    );
    this.staffer$ = this._currentStaffer.doc$.pipe(filterUndefined());
    this.storagePath$ = this.staffer$.pipe(map((staffer) => staffer.ref.path));
    this.profile$ = this.staffer$.pipe(
      map((staffer) => ({
        name: Staffer.onlineProfileName(staffer),
        profileImageURL: Staffer.onlineProfileImageURL(staffer),
      }))
    );

    const brand$ = this._currentBrand.doc$.pipe(filterUndefined());

    this.billingEntitiesEnabled$ = brand$.pipe(
      map((brand) => !!brand.settings.billing?.enableBillingEntitySelection)
    );

    this.billingEntitiesEnabled$
      .pipe(takeUntilDestroyed())
      .subscribe((enabled) => {
        const validators = enabled ? [Validators.required] : [];

        this.billingEntities.controls.map((control) => {
          const { registrationNumberType, registrationNumber } =
            control as BillingEntityFormGroup;

          registrationNumberType.setValidators(validators);
          registrationNumber.setValidators(validators);
          registrationNumberType.updateValueAndValidity();
          registrationNumber.updateValueAndValidity();
        });
      });

    this.practices$ = brand$.pipe(
      switchMap((brand) => Brand.practices$(brand)),
      shareReplayCold()
    );

    this.selectablePracticesByRow$ = combineLatest([
      this.practices$,
      defer(() => formControlChanges$(this.nzPracticeProviderDetails)),
    ]).pipe(
      map(([practices, rows = []]) =>
        NzPracticeProviderDetailsControls.selectablePracticesByRow(
          practices,
          rows
        )
      )
    );

    this.staffer$
      .pipe(take(1), takeUntil(this._onDestroy$))
      .subscribe((staffer) => {
        this.providerDetailControls.initProviderDetailsControls(staffer, {
          emitEvent: false,
        });
        this.billingEntityControls.initBillingEntityControls(staffer, {
          emitEvent: false,
        });
        this.nzPracticeProviderDetailControls.initNzPracticeProviderDetailsControls(
          staffer,
          { emitEvent: false }
        );

        this.form.patchValue({
          ...staffer,
          nzProviderDetails: staffer.metadata?.nz?.providerDetails,
          nzPracticeProviderDetails:
            staffer.metadata?.nz?.practiceProviderDetails,
          heidi: staffer.metadata?.heidi,
        });
      });

    this.breadcrumbs$ = combineLatest([brand$, this.staffer$]).pipe(
      map(([brand, staffer]) => [
        { label: 'Settings', path: '../../../' },
        { label: brand.name },
        { label: 'Staff', path: '../' },
        { label: staffer.user.name },
      ])
    );
  }

  ngOnDestroy(): void {
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }

  get providerDetails(): TypedFormArray<IProviderData> {
    return this.form.controls.providerDetails as TypedFormArray<IProviderData>;
  }

  get billingEntities(): TypedFormArray<IBillingEntity> {
    return this.form.controls.billingEntities as TypedFormArray<IBillingEntity>;
  }

  get nzProviderDetails(): TypedFormGroup<INzStafferProviderDetails> {
    return this.form.controls
      .nzProviderDetails as TypedFormGroup<INzStafferProviderDetails>;
  }

  get nzPracticeProviderDetails(): TypedFormArray<INzStafferPracticeProviderDetails> {
    return this.form.controls
      .nzPracticeProviderDetails as TypedFormArray<INzStafferPracticeProviderDetails>;
  }

  get accProviderDetails(): TypedFormGroup<AccProviderDetails> {
    return this.nzProviderDetails.controls
      .acc as TypedFormGroup<AccProviderDetails>;
  }

  isSelectedPracticeRef(
    practiceRef: DocumentReference<IPractice>,
    selectedPracticeRef: DocumentReference<IPractice>
  ): boolean {
    return isSameRef(practiceRef, selectedPracticeRef);
  }

  async submit(): Promise<void> {
    const staffer = await snapshot(this.staffer$);
    if (this.form.invalid || !staffer) {
      return;
    }
    const formData = this.form.getRawValue();
    await Firestore.patchDoc(staffer.ref, {
      ...omit(
        formData,
        'nzProviderDetails',
        'nzPracticeProviderDetails',
        'heidi'
      ),
      metadata: {
        ...staffer.metadata,
        nz: {
          ...staffer.metadata?.nz,
          providerDetails: formData.nzProviderDetails,
          practiceProviderDetails: formData.nzPracticeProviderDetails.filter(
            (details) => !!details.practiceRef
          ),
        },
        heidi: {
          ...staffer.metadata?.heidi,
          ...formData.heidi,
        },
      },
    });
    this._snackBar.open('Staffer updated');
  }

  async updateImageUrl(storagePath: string): Promise<void> {
    const staffer = await snapshot(this.staffer$);
    await Firestore.patchDoc(staffer.ref, {
      onlineProfile: { ...staffer.onlineProfile, profileImageURL: storagePath },
    });
  }
}
